// Sleep clustering + rollups. Shared by the Home tile and the /stats page so
// the numbers never disagree.
//
// "Cluster" = a group of sessions the parent thinks of as one event:
//   - A night cluster is a run of NIGHT-classified sessions with no day
//     session between them AND no awake gap > 6h. The 6h rule prevents
//     yesterday's night from bridging into tonight when no naps are logged
//     in the daytime between.
//   - A day cluster is always a single nap session — daytime sleeps don't
//     merge.
//
// Each cluster is then ATTRIBUTED to one calendar day:
//   - Night cluster → the day the last session ended on (Apple-Health model).
//     A 19:00→23:55 + 00:30→06:30 sequence ends in the morning, so the
//     whole 10h+ counts toward that morning's day.
//   - Day cluster → the day of the session's midpoint.
//
// This attribution is what every per-day stat hangs off. "Naps today",
// "last night", week-bar heights — all just `clusters where attributedDay
// === <date>`.
//
// `computeSleepStats` keeps a compact API for the Home tile; the richer
// `clusterSleepSessions` powers /stats.

import type { SleepSession } from '@shared/types.ts';

const NIGHT_START_MIN = 19 * 60;
const NIGHT_END_MIN = 7 * 60;
const GAP_SPLIT_SEC = 6 * 3600;

function midpointSec(s: SleepSession, nowSec: number): number {
  const end = s.ended_at ?? nowSec;
  return Math.floor((s.started_at + end) / 2);
}

function isNightMidpoint(midSec: number): boolean {
  const d = new Date(midSec * 1000);
  const mins = d.getHours() * 60 + d.getMinutes();
  return mins >= NIGHT_START_MIN || mins <= NIGHT_END_MIN;
}

function startOfDaySec(sec: number): number {
  const d = new Date(sec * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

function durationSec(s: SleepSession, nowSec: number): number {
  const end = s.ended_at ?? nowSec;
  return Math.max(0, end - s.started_at);
}

export type SleepCluster = {
  kind: 'night' | 'day';
  sessions: SleepSession[];
  /** Sum of session durations in minutes (in-progress counted up to now). */
  totalMinutes: number;
  /** True iff any session in the cluster is still in-progress. */
  hasInProgress: boolean;
  /** Local midnight (epoch sec) of the day this cluster belongs to. */
  attributedDayStartSec: number;
  /** End of the cluster (last session's ended_at, or `now` if in-progress).
   *  Useful for the "morning wake" cutoff between last night and today. */
  endSec: number;
};

export function clusterSleepSessions(
  sessions: SleepSession[],
  now: Date = new Date(),
): SleepCluster[] {
  const nowSec = Math.floor(now.getTime() / 1000);
  const sorted = [...sessions].sort((a, b) => a.started_at - b.started_at);

  type Building = {
    kind: 'night' | 'day';
    sessions: SleepSession[];
    totalMinutes: number;
    hasInProgress: boolean;
    endSec: number;
  };
  const acc: Building[] = [];
  let prevEndSec: number | null = null;

  for (const s of sorted) {
    const mid = midpointSec(s, nowSec);
    const kind: 'night' | 'day' = isNightMidpoint(mid) ? 'night' : 'day';
    const mins = Math.floor(durationSec(s, nowSec) / 60);
    const inProg = s.ended_at == null;
    const endSec = s.ended_at ?? nowSec;

    if (kind === 'day') {
      acc.push({ kind, sessions: [s], totalMinutes: mins, hasInProgress: inProg, endSec });
      prevEndSec = endSec;
      continue;
    }

    const last = acc[acc.length - 1];
    const gap = prevEndSec == null ? Infinity : s.started_at - prevEndSec;
    if (last && last.kind === 'night' && gap < GAP_SPLIT_SEC) {
      last.sessions.push(s);
      last.totalMinutes += mins;
      last.endSec = endSec;
      if (inProg) last.hasInProgress = true;
    } else {
      acc.push({ kind, sessions: [s], totalMinutes: mins, hasInProgress: inProg, endSec });
    }
    prevEndSec = endSec;
  }

  return acc.map((c) => ({
    ...c,
    attributedDayStartSec:
      c.kind === 'night'
        ? startOfDaySec(c.endSec)
        : startOfDaySec(midpointSec(c.sessions[0]!, nowSec)),
  }));
}

export type SleepStats = {
  lastNightMins: number;
  napsTodayMins: number;
  /** Non-null when the user is currently in a night-classified cluster
   *  (typically because there is an in-progress night sleep). */
  tonightSoFarMins: number | null;
};

export function computeSleepStats(
  sessions: SleepSession[],
  now: Date = new Date(),
): SleepStats {
  const nowSec = Math.floor(now.getTime() / 1000);
  const todayStart = startOfDaySec(nowSec);
  const clusters = clusterSleepSessions(sessions, now);

  let napsTodayMins = 0;
  for (const c of clusters) {
    if (c.kind === 'day' && c.attributedDayStartSec === todayStart) {
      napsTodayMins += c.totalMinutes;
    }
  }

  // Walk newest → oldest through night clusters. The most recent one is
  // either "tonight" (still going OR just-ended bedtime that started today
  // after 14:00) or "last night" (everything else — i.e. it was a previous
  // night that has already concluded). The first match decides tonight;
  // the next night cluster becomes "last night".
  //
  // This is what makes the Home tile useful at 00:04: tonight's bedtime
  // that ended at 23:38 is "last night" once the calendar rolls over, and
  // the cluster before it (Wed→Thu morning) is no longer surfaced. Apple
  // Health works the same way.
  let tonightSoFarMins: number | null = null;
  let lastNightMins = 0;
  let foundLast = false;
  for (let i = clusters.length - 1; i >= 0; i--) {
    const c = clusters[i];
    if (c.kind !== 'night') continue;
    const first = c.sessions[0]!;
    const startDay = startOfDaySec(first.started_at);
    const startHour = new Date(first.started_at * 1000).getHours();
    const isTonightCandidate =
      c.hasInProgress || (startDay === todayStart && startHour >= 14);

    if (isTonightCandidate && tonightSoFarMins === null) {
      tonightSoFarMins = c.totalMinutes;
      continue;
    }
    if (!foundLast) {
      lastNightMins = c.totalMinutes;
      foundLast = true;
      break;
    }
  }

  return { lastNightMins, napsTodayMins, tonightSoFarMins };
}
